import React, { useRef } from 'react'
import PropTypes from 'prop-types'
import { connectStateResults, connectPagination } from 'react-instantsearch-dom'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import GroupEventCard from '@components/group-event-card'
import Pagination from '@components/pagination'
import defaults from '@config/defaults'
import LinearProgress from '@material-ui/core/LinearProgress'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { NoResultsPage } from './NoResultsPage'
import useStyles from './results.styles'

const LoadingIndicator = connectStateResults(
  ({ searching }) => (searching ? <LinearProgress color="secondary" /> : null),
)

const HitsCount = connectStateResults(
  ({ searchResults: res }) => `${res.nbHits}`,
)
const PaginationHeader = ({
  nbPages,
  currentRefinement,
  hitsLength,
}) => {
  const classes = useStyles()

  const itemsPerPage = defaults.groupEventsPerPage
  /* eslint-disable no-nested-ternary */
  const startHit = (currentRefinement - 1) * itemsPerPage + 1
  const endHit = hitsLength > 1
    ? nbPages !== currentRefinement
      ? `-${currentRefinement * itemsPerPage}`
      : <span>-<HitsCount /></span>
    : null

  return nbPages > 1 && (
    <Typography variant="body2" component="h1" className={classes.paginationHeader}>
      Showing {startHit}{endHit} of <HitsCount />
    </Typography>
  )
}

PaginationHeader.propTypes = {
  nbPages: PropTypes.number,
  currentRefinement: PropTypes.number,
  hitsLength: PropTypes.number,
}

const CustomPagination = ({
  nbPages,
  currentRefinement,
  refine,
  scrollRef,
}) => {
  const itemsPerPage = defaults.groupEventsPerPage
  const classes = useStyles()

  const handlePaginationClick = (e, newOffset, newPage) => {
    refine(newPage)
    scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return nbPages > 1 ? (
    <div className={classes.pagination}>
      <LoadingIndicator />
      <Pagination
        limit={itemsPerPage}
        offset={(currentRefinement - 1) * itemsPerPage}
        total={itemsPerPage * nbPages}
        onClick={handlePaginationClick}
        padding={[0, 0, 3.5]}
      />
    </div>
  ) : null
}

CustomPagination.propTypes = {
  nbPages: PropTypes.number,
  currentRefinement: PropTypes.number,
  refine: PropTypes.func,
  scrollRef: PropTypes.object,
}

const SearchResults = ({
  hits,
  setCardHoveredHit,
  showRefiners,
  scrollRef,
}) => {
  const xsMobile = useMediaQuery('(max-width:600px)')
  const classes = useStyles({ showRefiners })

  return (
    <div
      className={classes.mapViewListWrapper}
      ref={scrollRef}
    >
      <Grid
        item
        xs={12}
      >
        {hits.map((hit) => {
          return (
            <div key={hit.objectID} style={{ borderBottom: '1px solid #E0E0E0', padding: '15px 0px' }}>
              <GroupEventCard
                {...hit}
                withPrice
                cardStyle="row"
                cardHoverEnterHandler={() => {
                  setCardHoveredHit(hit)
                }}
                cardHoverLeaveHandler={() => {
                  setCardHoveredHit(null)
                }}
                squareImage={xsMobile}
                source="event-search-directory"
              />
            </div>
          )
        })}
      </Grid>
    </div>
  )
}

SearchResults.propTypes = {
  hits: PropTypes.array,
  setCardHoveredHit: PropTypes.func,
  showRefiners: PropTypes.bool,
  scrollRef: PropTypes.object,
}

export const GroupEventsSearchPaginatedResultsToConnect = ({
  hits,
  nbPages,
  currentRefinement,
  refine,
  setCardHoveredHit,
  resultsString,
  showRefiners,
}) => {
  const scrollRef = useRef(null)
  return hits.length > 0 ? (
    <>
      <PaginationHeader
        nbPages={nbPages}
        currentRefinement={currentRefinement}
        hitsLength={hits.length}
      />
      <SearchResults
        scrollRef={scrollRef}
        hits={hits}
        setCardHoveredHit={setCardHoveredHit}
        showRefiners={showRefiners}
      />
      <CustomPagination
        nbPages={nbPages}
        currentRefinement={currentRefinement}
        refine={refine}
        scrollRef={scrollRef}
      />
    </>
  ) : (
    <NoResultsPage resultsString={resultsString} />
  )
}

GroupEventsSearchPaginatedResultsToConnect.propTypes = {
  hits: PropTypes.array,
  nbPages: PropTypes.number,
  currentRefinement: PropTypes.number,
  refine: PropTypes.func,
  setCardHoveredHit: PropTypes.func,
  resultsString: PropTypes.string,
  showRefiners: PropTypes.bool,
}

const GroupEventsSearchPaginatedResults = connectPagination(
  GroupEventsSearchPaginatedResultsToConnect,
)

export default GroupEventsSearchPaginatedResults
